import { ChecklistLookupField } from "@/components/checklists/checklist-lookup-field";
import { useTaskChecklistWorkspace, isFloatingChecklistTarget } from "@/components/checklists/task-checklist-workspace";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  CheckSquare, Square, Calendar, Link as LinkIcon, Trash2, Pencil, Plus, Search, ListTodo,
  Target, BookOpen, Milestone as MilestoneIcon, Filter, FileText, List, LayoutGrid, MoreVertical,
  Star, ChevronDown, ChevronRight, GripVertical, Tag, AlertCircle, Clock, Share2, Users, UserPlus,
  File as FileIcon, Download, X, Sparkles, Flag, ExternalLink, Paperclip, MessageSquare, Activity,
  Eye, EyeOff, CalendarPlus, Upload, HardDrive, FolderOpen,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format, isPast, parseISO, endOfDay, startOfDay } from "date-fns";
import { formatDateInput, parseLocalDate } from "@/lib/dateUtils";
import DocumentUploadButton, { uploadOneDocument } from "@/components/document-upload-button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { sanitizeCategory } from "@/lib/categoryUtils";
import {
  SortablePendingSubTaskItem, FriendPicker, AssistantPicker, TagEditor, RecurrenceSection, LinkEntitySelectField,
  AssignToSelect, AssignedToOtherField, resolveTaskAssignment,
} from "./task-form-shared";
import {
  taskFormSchema,
  type TaskFormData, type TaskFormInput, type Goal, type JournalEntry, type Category,
  type Milestone, type FriendWithUser, type AssistantWithUser,
  type PendingSubTask, type PendingTaskMilestone, type Task, type SubTask,
  type KanbanColumn, type TaskProgressEntryWithUser, type Attachment,
} from "./task-types";

const EMPTY_SUBTASKS: SubTask[] = [];

export function CreateTaskModal({
  open,
  onOpenChange,
  goals,
  journalEntries,
  initialColumnId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goals: Goal[];
  journalEntries: JournalEntry[];
  initialColumnId?: string | null;
}) {
  const [linkType, setLinkType] = useState<"none" | "goal" | "milestone" | "journal">("none");
  const [selectedGoalId, setSelectedGoalId] = useState<string>("");

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: allMilestones = [] } = useQuery<(Milestone & { goalTitle: string })[]>({
    queryKey: ["/api/milestones"],
    enabled: open,
  });

  const [currentTag, setCurrentTag] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [assignmentType, setAssignmentType] = useState<"Me" | "Friend" | "Assistant" | "Other">("Me");
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(null);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [friendSearchQuery, setFriendSearchQuery] = useState("");

  const { data: friends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
    enabled: assignmentType === "Friend",
  });

  const { data: assistants = [] } = useQuery<AssistantWithUser[]>({
    queryKey: ["/api/assistants"],
    enabled: open,
  });

  const { toast } = useToast();
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<string>("weekly");
  const [recurrenceEndType, setRecurrenceEndType] = useState<"count" | "date">("count");
  const [recurrenceCount, setRecurrenceCount] = useState<number | undefined>(undefined);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>("");
  const [pendingSubTasks, setPendingSubTasks] = useState<PendingSubTask[]>([]);
  const [newPendingText, setNewPendingText] = useState("");
  const [nextPendingRowNum, setNextPendingRowNum] = useState(1);
  const [pendingTaskMilestones, setPendingTaskMilestones] = useState<PendingTaskMilestone[]>([]);
  const [newMilestoneText, setNewMilestoneText] = useState("");
  const [wantSchedule, setWantSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState<Date | undefined>(undefined);
  const [schedTime, setSchedTime] = useState("09:00");
  const [schedDuration, setSchedDuration] = useState("60");
  const [schedRepeat, setSchedRepeat] = useState(false);
  const [pendingUrlAttachments, setPendingUrlAttachments] = useState<{ name: string; url: string }[]>([]);
  const [newUrlAttachmentName, setNewUrlAttachmentName] = useState("");
  const [newUrlAttachmentUrl, setNewUrlAttachmentUrl] = useState("");
  const [urlAttachmentsExpanded, setUrlAttachmentsExpanded] = useState(false);
  const [pendingMediaAttachments, setPendingMediaAttachments] = useState<{ id: string; name: string }[]>([]);
  const [pendingDriveAttachments, setPendingDriveAttachments] = useState<{ driveFileId: string; name: string }[]>([]);
  // Holds lightweight File handles only — the file contents are read and
  // uploaded exclusively at save time, so unsaved tasks never store anything.
  const [pendingFileUploads, setPendingFileUploads] = useState<{ file: File; destination: 'media' | 'drive' }[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [attachPickerView, setAttachPickerView] = useState<'media' | 'drive' | null>(null);

  // A newly opened Create Task dialog must always start completely blank.
  // The dialog stays mounted between uses (only the `open` prop changes), so
  // any state from an earlier, unsaved attempt — attachments included —
  // would otherwise leak into the next task.
  useEffect(() => {
    if (open) {
      setPendingUrlAttachments([]);
      setPendingMediaAttachments([]);
      setPendingDriveAttachments([]);
      setPendingFileUploads([]);
      setNewUrlAttachmentName("");
      setNewUrlAttachmentUrl("");
      setUrlAttachmentsExpanded(false);
      setAttachPickerView(null);
      setPendingSubTasks([]);
      setNewPendingText("");
      setNextPendingRowNum(1);
      setPendingTaskMilestones([]);
      setNewMilestoneText("");
      setWantSchedule(false);
      setSchedDate(undefined);
      setSchedTime("09:00");
      setSchedDuration("60");
      setSchedRepeat(false);
      form.setValue("checklistId", null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: schedDayEvents = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar", schedDate?.toISOString().split("T")[0], "create-modal"],
    queryFn: async () => {
      if (!schedDate) return [];
      const res = await fetch(`/api/calendar?startDate=${startOfDay(schedDate).toISOString()}&endDate=${endOfDay(schedDate).toISOString()}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!schedDate && wantSchedule,
  });

  const { data: pickerMediaFiles = [] } = useQuery<{ id: string; name: string; fileType: string }[]>({
    queryKey: ["/api/media"],
    enabled: attachPickerView === 'media' && urlAttachmentsExpanded,
  });

  const { data: pickerDriveFiles = [] } = useQuery<{ id: string; driveFileId: string; name: string; mimeType: string }[]>({
    queryKey: ["/api/google-drive/files"],
    enabled: attachPickerView === 'drive' && urlAttachmentsExpanded,
  });

  const pendingSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Per-milestone "add sub-task" input state (which pending milestone is
  // adding, and the draft text).
  const [addingSubtaskForMilestone, setAddingSubtaskForMilestone] = useState<string | null>(null);
  const [newMilestoneSubtaskText, setNewMilestoneSubtaskText] = useState("");
  const submitMilestoneSubtask = (milestoneId: string) => {
    const text = newMilestoneSubtaskText.trim();
    if (!text) return;
    setPendingSubTasks(prev => [...prev, {
      id: crypto.randomUUID(),
      text,
      dueDate: null,
      rowNumber: nextPendingRowNum,
      milestoneId,
    }]);
    setNextPendingRowNum(prev => prev + 1);
    setNewMilestoneSubtaskText("");
  };

  const handleAddPending = () => {
    if (newPendingText.trim()) {
      setPendingSubTasks(prev => [...prev, {
        id: crypto.randomUUID(),
        text: newPendingText.trim(),
        dueDate: null,
        rowNumber: nextPendingRowNum,
      }]);
      setNextPendingRowNum(prev => prev + 1);
      setNewPendingText("");
    }
  };

  const handlePendingDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = pendingSubTasks.findIndex(st => st.id === active.id);
      const newIndex = pendingSubTasks.findIndex(st => st.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setPendingSubTasks(prev => arrayMove(prev, oldIndex, newIndex));
      }
    }
  };

  const handlePendingDelete = (id: string) => {
    setPendingSubTasks(prev => prev.filter(st => st.id !== id));
  };

  const handlePendingTextChange = (id: string, text: string) => {
    setPendingSubTasks(prev => prev.map(st => st.id === id ? { ...st, text } : st));
  };

  const handlePendingDateChange = (id: string, date: string | null) => {
    setPendingSubTasks(prev => prev.map(st => st.id === id ? { ...st, dueDate: date } : st));
  };

  const addUrlAttachment = () => {
    const name = newUrlAttachmentName.trim();
    const url = newUrlAttachmentUrl.trim();
    if (!name || !url) return;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        toast({ title: "Invalid URL", description: "URL must start with http:// or https://", variant: "destructive" });
        return;
      }
    } catch {
      toast({ title: "Invalid URL", description: "Please enter a valid URL", variant: "destructive" });
      return;
    }
    setPendingUrlAttachments(prev => [...prev, { name, url }]);
    setNewUrlAttachmentName("");
    setNewUrlAttachmentUrl("");
  };

  const handleFileUpload = async (destination: 'media' | 'drive') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 50 * 1024 * 1024) {
        toast({ title: "File too large", description: "Maximum file size is 50MB", variant: "destructive" });
        return;
      }
      // Do NOT read or upload the file yet — nothing may be stored anywhere
      // until the task is actually saved. Keep only the File handle; the
      // contents are read and uploaded after the task is created.
      setPendingFileUploads(prev => [...prev, { file, destination }]);
    };
    input.click();
  };

  const form = useForm<TaskFormInput, any, TaskFormData>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: undefined,
      completed: false,
      category: "Uncategorized",
      priority: "medium",
      tags: [],
      assignedTo: "Me",
      assignedToOther: undefined,
      dueDate: undefined,
      linkType: "none",
      goalId: undefined,
      milestoneId: undefined,
      journalEntryId: undefined,
      checklistId: null,
    },
  });

  // Idempotency key: generated once per submission attempt and reused on
  // network-error retries so the server can deduplicate. Cleared after a
  // confirmed success or a definitive HTTP error (4xx/5xx). Kept alive on
  // network failures so the next click reuses the same key.
  const idempotencyKeyRef = useRef<string | null>(null);

  const processAttachmentsInBackground = useCallback((
    taskId: string,
    attachments: {
      urls: { name: string; url: string }[];
      media: { id: string; name: string }[];
      drive: { driveFileId: string; name: string }[];
      files: { file: File; destination: "media" | "drive" }[];
    },
  ) => {
    const jobs: { label: string; promise: Promise<unknown> }[] = [
      ...attachments.urls.map((attachment) => ({
        label: attachment.name,
        promise: apiRequest("POST", `/api/tasks/${taskId}/url`, attachment),
      })),
      ...attachments.media.map((attachment) => ({
        label: attachment.name,
        promise: apiRequest("POST", `/api/tasks/${taskId}/media`, { mediaId: attachment.id }),
      })),
      ...attachments.drive.map((attachment) => ({
        label: attachment.name,
        promise: apiRequest("POST", `/api/tasks/${taskId}/drive`, { driveFileId: attachment.driveFileId }),
      })),
      // uploadOneDocument retains its cleanup behavior if attaching an
      // uploaded media/Drive item fails.
      ...attachments.files.map((attachment) => ({
        label: attachment.file.name,
        promise: uploadOneDocument(attachment.file, attachment.destination, "task", taskId),
      })),
    ];

    if (jobs.length === 0) return;

    toast({
      title: "Processing attachments",
      description: `${jobs.length} attachment${jobs.length === 1 ? "" : "s"} will be added in the background.`,
    });

    // Start every operation at once. In particular, one stalled upload must
    // not stop URL, media, Drive, or subsequent local-file attachments.
    void Promise.allSettled(jobs.map((job) => job.promise)).then((outcomes) => {
      const failed = outcomes.flatMap((outcome, index) =>
        outcome.status === "rejected" ? [jobs[index].label] : []
      );
      const succeeded = jobs.length - failed.length;

      queryClient.invalidateQueries({ queryKey: [`/api/tasks/${taskId}/attachments`] });
      queryClient.invalidateQueries({ queryKey: ["/api/media"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });

      if (failed.length === 0) {
        toast({
          title: "Attachments added",
          description: `${succeeded} attachment${succeeded === 1 ? "" : "s"} added to the task.`,
        });
      } else {
        toast({
          title: "Some attachments failed",
          description: `${succeeded} added; failed: ${failed.join(", ")}`,
          variant: "destructive",
        });
      }
    });
  }, [toast]);

  const createTaskMutation = useMutation({
    mutationFn: async (data: TaskFormData) => {
      try {
        // Determine assignedTo and assignedToUserId based on assignment type
        // (shared guard: Friend with no selection / Other with a blank name
        // safely fall back to "Me").
        const {
          assignedTo: assignedToValue,
          assignedToOther: assignedToOtherValue,
          assignedToUserId: assignedToUserIdValue,
        } = resolveTaskAssignment({
          assignmentType,
          selectedFriendId,
          friends,
          selectedAssistantId,
          assistants,
          assignedToOther: data.assignedToOther,
        });

        // Build only the fields that match insertTaskSchema
        const taskData: any = {
          title: data.title,
          description: data.description || null,
          completed: data.completed || false,
          category: data.category || "Uncategorized",
          priority: data.priority || "medium",
          tags: tags,
          assignedTo: assignedToValue,
          assignedToOther: assignedToOtherValue,
          assignedToUserId: assignedToUserIdValue,
          dueDate: data.dueDate ? parseLocalDate(data.dueDate).toISOString() : null,
          columnId: initialColumnId || null,
          checklistId: data.checklistId ?? null,
        };

        // Only add valid link fields based on linkType
        if (linkType === "goal" && data.goalId) {
          taskData.goalId = data.goalId;
          taskData.milestoneId = null;
          taskData.journalEntryId = null;
        } else if (linkType === "milestone" && data.milestoneId) {
          taskData.goalId = null;
          taskData.milestoneId = data.milestoneId;
          taskData.journalEntryId = null;
        } else if (linkType === "journal" && data.journalEntryId) {
          taskData.goalId = null;
          taskData.milestoneId = null;
          taskData.journalEntryId = data.journalEntryId;
        } else {
          // No links
          taskData.goalId = null;
          taskData.milestoneId = null;
          taskData.journalEntryId = null;
        }

        // Add recurrence data if enabled
        if (isRecurring) {
          taskData.isRecurring = true;
          taskData.recurrenceFrequency = recurrenceFrequency;
          if (recurrenceEndType === "count") {
            taskData.recurrenceCount = recurrenceCount;
            taskData.recurrenceEndDate = null;
          } else {
            taskData.recurrenceCount = null;
            taskData.recurrenceEndDate = recurrenceEndDate ? parseLocalDate(recurrenceEndDate).toISOString() : null;
          }
        } else {
          taskData.isRecurring = false;
          taskData.recurrenceFrequency = null;
          taskData.recurrenceCount = null;
          taskData.recurrenceEndDate = null;
        }

        // Generate a key once per submission attempt; reuse it on network-
        // error retries so the server can deduplicate the request.
        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current = crypto.randomUUID();
        }
        console.log('Creating task with data:', taskData);
        const response = await apiRequest("POST", "/api/tasks", taskData, {
          "Idempotency-Key": idempotencyKeyRef.current,
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          console.error('Create task error response:', errorData);
          throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
        }
        
        return response.json();
      } catch (error: any) {
        console.error('Create Task Error:', error);
        throw error;
      }
    },
    onSuccess: async (result) => {
      // Submission confirmed — discard the idempotency key so a future
      // open of this modal starts a fresh submission.
      idempotencyKeyRef.current = null;
      // Auto-share the task if a friend was selected
      if (assignmentType === "Friend" && selectedFriendId) {
        try {
          const shareResponse = await apiRequest("POST", "/api/shares/tasks", {
            taskId: result.id,
            sharedWithId: selectedFriendId,
            permission: "edit"
          });
          if (shareResponse.ok) {
            toast({
              title: "Task shared",
              description: `Task automatically shared with the assigned friend.`,
            });
          }
        } catch (error) {
          console.error('Failed to auto-share task:', error);
        }
      }

      // Create any pending task milestones, remembering the real id each
      // pending milestone was given so milestone-scoped sub-tasks can link.
      const milestoneIdMap = new Map<string, string>();
      if (pendingTaskMilestones.length > 0) {
        for (let i = 0; i < pendingTaskMilestones.length; i++) {
          const pm = pendingTaskMilestones[i];
          try {
            const res = await apiRequest("POST", `/api/tasks/${result.id}/task-milestones`, {
              title: pm.title,
              dueDate: pm.dueDate ? parseLocalDate(pm.dueDate).toISOString() : null,
              displayOrder: i,
            });
            const created = await res.json();
            if (created?.id) milestoneIdMap.set(pm.id, created.id);
          } catch (err) {
            console.error('Failed to create task milestone:', err);
          }
        }
        queryClient.invalidateQueries({ queryKey: ["/api/tasks", result.id, "task-milestones"] });
      }

      // Create any pending sub-tasks
      if (pendingSubTasks.length > 0) {
        for (let i = 0; i < pendingSubTasks.length; i++) {
          const ps = pendingSubTasks[i];
          try {
            const realMilestoneId = ps.milestoneId ? milestoneIdMap.get(ps.milestoneId) : undefined;
            await apiRequest("POST", `/api/tasks/${result.id}/subtasks`, {
              text: ps.text,
              dueDate: ps.dueDate ? parseLocalDate(ps.dueDate).toISOString() : null,
              displayOrder: i,
              ...(realMilestoneId ? { milestoneId: realMilestoneId } : {}),
            });
          } catch (err) {
            console.error('Failed to create sub-task:', err);
          }
        }
        queryClient.invalidateQueries({ queryKey: ["/api/subtasks/all"] });
      }
      
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/assigned-to-me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/tasks/sent"] });
      form.reset();
      setTags([]);
      setCurrentTag("");
      setAssignmentType("Me");
      setSelectedFriendId(null);
      setLinkType("none");
      setSelectedGoalId("");
      setIsRecurring(false);
      setRecurrenceFrequency("weekly");
      setRecurrenceEndType("count");
      setRecurrenceCount(5);
      setRecurrenceEndDate("");
      setPendingSubTasks([]);
      setNewPendingText("");
      setNextPendingRowNum(1);
      setPendingTaskMilestones([]);
      setNewMilestoneText("");
      // Create calendar event if scheduling was requested
      if (wantSchedule && schedDate) {
        try {
          const [hours, mins] = schedTime.split(":").map(Number);
          const start = new Date(schedDate);
          start.setHours(hours, mins, 0, 0);
          const end = new Date(start);
          end.setMinutes(end.getMinutes() + Number(schedDuration));
          const event = { title: result.title, taskId: result.id, startTime: start.toISOString(), endTime: end.toISOString(), eventType: "task", color: "#6366f1", repeatIfIncomplete: schedRepeat };
          await apiRequest("POST", "/api/calendar", event);
          queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
          toast({ title: "Added to Calendar", description: `"${result.title}" scheduled.` });
        } catch (_) { /* calendar creation is best-effort */ }
        setWantSchedule(false);
        setSchedDate(undefined);
        setSchedRepeat(false);
      }
      // Snapshot before resetting the dialog. Attachment work deliberately
      // runs after this mutation has settled: a slow object-storage PUT must
      // never leave a successfully-created task stuck in "Creating...".
      const attachments = {
        urls: pendingUrlAttachments,
        media: pendingMediaAttachments,
        drive: pendingDriveAttachments,
        files: pendingFileUploads,
      };
      setPendingUrlAttachments([]);
      setPendingMediaAttachments([]);
      setPendingDriveAttachments([]);
      setPendingFileUploads([]);
      onOpenChange(false);
      toast({
        title: "Task created",
        description: `"${result.title}" was created successfully.`,
      });
      processAttachmentsInBackground(result.id, attachments);
    },
    onError: (error: any) => {
      // HTTP errors (server definitively rejected the request) — clear the
      // key so the next attempt is treated as a fresh submission.
      // Network errors (no status) keep the key so a retry can be
      // deduplicated if the server already processed the first attempt.
      if (error?.status != null) {
        idempotencyKeyRef.current = null;
      }
      toast({ title: "Failed to create task", description: error?.message || "An unexpected error occurred. Please try again.", variant: "destructive" });
    },
  });

  // Ref-based guard: a fast double-click can fire the submit handler twice
  // before React re-renders the disabled button, creating duplicate tasks.
  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createTaskMutation.isPending) submitLockRef.current = false;
  }, [createTaskMutation.isPending]);

  // When the modal closes (user cancelled or navigated away), discard any
  // pending idempotency key so a future open starts a fresh submission.
  useEffect(() => {
    if (!open) {
      idempotencyKeyRef.current = null;
    }
  }, [open]);
  const onSubmit = (data: TaskFormData) => {
    if (submitLockRef.current || createTaskMutation.isPending) return;
    submitLockRef.current = true;
    createTaskMutation.mutate(data);
  };

  const { isChecklistOpen } = useTaskChecklistWorkspace();

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!isChecklistOpen}>
      <DialogContent
        className="w-screen h-screen m-0 rounded-none overflow-y-auto"
        data-testid="dialog-create-task"
        onInteractOutside={(e) => { if (isFloatingChecklistTarget(e.target) || isChecklistOpen) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>Add a standalone task or link it to a goal, milestone, or journal entry.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
            const firstError = Object.values(errors)[0];
            const msg = (firstError as any)?.message as string | undefined;
            toast({ title: "Please check the form", description: msg || "Some required fields are missing.", variant: "destructive" });
          })} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Task title" data-testid="input-task-title" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} value={field.value || ""} placeholder="Task description (optional)" rows={3} data-testid="input-task-description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="checklistId"
              render={({ field }) => (
                <FormItem>
                  <ChecklistLookupField value={field.value ?? null} onChange={(id) => field.onChange(id)} enabled={open} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || "Uncategorized"}>
                    <FormControl>
                      <SelectTrigger data-testid="select-task-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                      <CategorySelectGroups categories={categories.filter(cat => cat.name !== "Uncategorized")} />
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || "medium"}>
                    <FormControl>
                      <SelectTrigger data-testid="select-task-priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date {isRecurring && <span className="text-destructive">* (Required for recurring tasks)</span>}</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" data-testid="input-task-due-date" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recurring Task Section */}
            <RecurrenceSection
              isRecurring={isRecurring}
              onToggleRecurring={() => setIsRecurring(!isRecurring)}
              recurrenceFrequency={recurrenceFrequency}
              onRecurrenceFrequencyChange={setRecurrenceFrequency}
              recurrenceEndType={recurrenceEndType}
              onRecurrenceEndTypeChange={setRecurrenceEndType}
              recurrenceCount={recurrenceCount}
              onRecurrenceCountChange={setRecurrenceCount}
              recurrenceEndDate={recurrenceEndDate}
              onRecurrenceEndDateChange={setRecurrenceEndDate}
              frequencyOptions={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "bi-weekly", label: "Bi-weekly (Every 2 weeks)" },
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly (Every 3 months)" },
                { value: "yearly", label: "Yearly" },
              ]}
            />

            <TagEditor
              tags={tags}
              onTagsChange={setTags}
              currentTag={currentTag}
              onCurrentTagChange={setCurrentTag}
              inputTestId="input-task-tag"
              addButtonTestId="button-add-tag"
              removeTagTestIdPrefix="button-remove-tag"
            />

            <AssignToSelect
              assignmentType={assignmentType}
              onAssignmentTypeChange={setAssignmentType}
              onSelectedFriendIdChange={setSelectedFriendId}
              onFriendSearchQueryChange={setFriendSearchQuery}
              onSelectedAssistantIdChange={setSelectedAssistantId}
              hasAssistants={assistants.length > 0}
              form={form}
              testId="select-assigned-to"
            />

            {assignmentType === "Friend" && (
              <FriendPicker
                friends={friends}
                friendSearchQuery={friendSearchQuery}
                onFriendSearchQueryChange={setFriendSearchQuery}
                selectedFriendId={selectedFriendId}
                onSelectFriend={setSelectedFriendId}
                friendTestIdPrefix="select-friend"
                selectedHint="This task will be automatically shared with the selected friend."
              />
            )}

            {assignmentType === "Assistant" && (
              <AssistantPicker
                assistants={assistants}
                selectedAssistantId={selectedAssistantId}
                onSelectAssistant={setSelectedAssistantId}
                testIdPrefix="select-assistant"
              />
            )}

            {assignmentType === "Other" && (
              <AssignedToOtherField control={form.control} testId="input-assigned-to-other" />
            )}

            {/* Link To Section */}
            <div className="space-y-3">
              <FormLabel>Link To</FormLabel>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="radio-link-type">
                {([
                  { value: "none", label: "None", icon: <X className="h-4 w-4" /> },
                  { value: "goal", label: "Goal", icon: <Target className="h-4 w-4" /> },
                  { value: "milestone", label: "Milestone", icon: <MilestoneIcon className="h-4 w-4" /> },
                  { value: "journal", label: "Journal", icon: <BookOpen className="h-4 w-4" /> },
                ] as const).map(({ value, label, icon }) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`radio-link-${value}`}
                    onClick={() => setLinkType(value)}
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                      linkType === value
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {linkType === "goal" && (
              <LinkEntitySelectField
                control={form.control}
                name="goalId"
                label="Select Goal"
                placeholder="Select a goal"
                testId="select-goal"
                options={goals.map((goal) => ({ id: goal.id, title: goal.title }))}
              />
            )}

            {linkType === "milestone" && (
              <LinkEntitySelectField
                control={form.control}
                name="milestoneId"
                label="Select Milestone"
                placeholder={allMilestones.length === 0 ? "No milestones found — create one in a Goal first" : "Select a milestone"}
                testId="select-milestone"
                options={allMilestones.map((milestone) => ({ id: milestone.id, title: milestone.title, subtitle: milestone.goalTitle }))}
                emptyMessage="No milestones found. Add milestones to your goals first."
              />
            )}

            {linkType === "journal" && (
              <LinkEntitySelectField
                control={form.control}
                name="journalEntryId"
                label="Select Journal Entry"
                placeholder="Select a journal entry"
                testId="select-journal-entry"
                options={journalEntries.map((entry) => ({ id: entry.id, title: entry.title }))}
              />
            )}

            {/* Task Milestones Section */}
            <div className="space-y-3">
              <FormLabel>Milestones</FormLabel>
              {pendingTaskMilestones.length > 0 && (
                <div className="space-y-2">
                  {pendingTaskMilestones.map((m, idx) => (
                    <div key={m.id} className="space-y-1.5">
                      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                        <Flag className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="flex-1 text-sm">{m.title}</span>
                        {m.dueDate && (
                          <span className="text-xs text-muted-foreground">{format(new Date(m.dueDate), "MMM d")}</span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                          title="Add sub-task to this milestone"
                          onClick={() => {
                            setAddingSubtaskForMilestone(prev => prev === m.id ? null : m.id);
                            setNewMilestoneSubtaskText("");
                          }}
                          data-testid={`button-add-subtask-pending-milestone-${m.id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            setPendingTaskMilestones(prev => prev.filter((_, i) => i !== idx));
                            setPendingSubTasks(prev => prev.filter(st => st.milestoneId !== m.id));
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {pendingSubTasks.filter(st => st.milestoneId === m.id).map(st => (
                        <div key={st.id} className="ml-6 flex items-center gap-2 rounded-md border bg-background px-3 py-1.5" data-testid={`pending-milestone-subtask-${st.id}`}>
                          <span className="flex-1 text-sm">{st.text}</span>
                          <Input
                            type="date"
                            value={st.dueDate || ""}
                            onChange={(e) => handlePendingDateChange(st.id, e.target.value || null)}
                            className="h-7 w-36 text-sm"
                            data-testid={`input-pending-milestone-subtask-date-${st.id}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => handlePendingDelete(st.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      {addingSubtaskForMilestone === m.id && (
                        <div className="ml-6 flex gap-1.5">
                          <Input
                            autoFocus
                            value={newMilestoneSubtaskText}
                            onChange={(e) => setNewMilestoneSubtaskText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                submitMilestoneSubtask(m.id);
                              } else if (e.key === "Escape") {
                                setAddingSubtaskForMilestone(null);
                              }
                            }}
                            placeholder="Add sub-task to this milestone..."
                            className="h-7 text-sm"
                            data-testid={`input-add-subtask-pending-milestone-${m.id}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => submitMilestoneSubtask(m.id)}
                            disabled={!newMilestoneSubtaskText.trim()}
                            data-testid={`button-submit-subtask-pending-milestone-${m.id}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newMilestoneText}
                  onChange={(e) => setNewMilestoneText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newMilestoneText.trim()) {
                        setPendingTaskMilestones(prev => [...prev, { id: crypto.randomUUID(), title: newMilestoneText.trim(), dueDate: null }]);
                        setNewMilestoneText("");
                      }
                    }
                  }}
                  placeholder="Add milestone..."
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newMilestoneText.trim()) {
                      setPendingTaskMilestones(prev => [...prev, { id: crypto.randomUUID(), title: newMilestoneText.trim(), dueDate: null }]);
                      setNewMilestoneText("");
                    }
                  }}
                  disabled={!newMilestoneText.trim()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <FormLabel>Sub Tasks</FormLabel>
              <DndContext
                sensors={pendingSensors}
                collisionDetection={closestCenter}
                onDragEnd={handlePendingDragEnd}
              >
                <SortableContext
                  items={pendingSubTasks.filter(st => !st.milestoneId).map(st => st.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2" data-testid="pending-subtasks-list">
                    {pendingSubTasks.filter(st => !st.milestoneId).map((subTask) => (
                      <SortablePendingSubTaskItem
                        key={subTask.id}
                        subTask={subTask}
                        onTextChange={handlePendingTextChange}
                        onDateChange={handlePendingDateChange}
                        onDelete={handlePendingDelete}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <div className="flex gap-2">
                <Input
                  value={newPendingText}
                  onChange={(e) => setNewPendingText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPending();
                    }
                  }}
                  placeholder="Add sub-task..."
                  data-testid="input-new-pending-subtask"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddPending}
                  disabled={!newPendingText.trim()}
                  data-testid="button-add-pending-subtask"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            {/* ── Attachments section ── */}
            <div className="border rounded-md overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                onClick={() => { setUrlAttachmentsExpanded(!urlAttachmentsExpanded); setAttachPickerView(null); }}
              >
                <Paperclip className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm font-medium">Attachments</span>
                {(pendingUrlAttachments.length + pendingMediaAttachments.length + pendingDriveAttachments.length) > 0 && (
                  <span className="ml-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                    {pendingUrlAttachments.length + pendingMediaAttachments.length + pendingDriveAttachments.length}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">{urlAttachmentsExpanded ? "▲ hide" : "▼ show"}</span>
              </button>
              {urlAttachmentsExpanded && (
                <div className="border-t p-4 space-y-3 bg-muted/20">

                  {/* Pending attachments list */}
                  {[
                    ...pendingUrlAttachments.map((a, i) => ({ key: `url-${i}`, name: a.name, sub: a.url, kind: 'url' as const, removeIdx: i })),
                    ...pendingMediaAttachments.map((a, i) => ({ key: `media-${i}`, name: a.name, sub: 'Media Library', kind: 'media' as const, removeIdx: i })),
                    ...pendingDriveAttachments.map((a, i) => ({ key: `drive-${i}`, name: a.name, sub: 'Google Drive', kind: 'drive' as const, removeIdx: i })),
                    ...pendingFileUploads.map((a, i) => ({ key: `file-${i}`, name: a.file.name, sub: a.destination === 'media' ? 'Will upload to Media when saved' : 'Will upload to Drive when saved', kind: 'file' as const, removeIdx: i })),
                  ].map((att) => (
                    <div key={att.key} className="flex items-center gap-2 bg-background border rounded-md p-2">
                      {att.kind === 'url'
                        ? <LinkIcon className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                        : att.kind === 'media'
                        ? <FileIcon className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                        : <HardDrive className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{att.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{att.sub}</p>
                      </div>
                      <Button
                        type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0"
                        onClick={() => {
                          if (att.kind === 'url') setPendingUrlAttachments(prev => prev.filter((_, idx) => idx !== att.removeIdx));
                          else if (att.kind === 'media') setPendingMediaAttachments(prev => prev.filter((_, idx) => idx !== att.removeIdx));
                          else if (att.kind === 'file') setPendingFileUploads(prev => prev.filter((_, idx) => idx !== att.removeIdx));
                          else setPendingDriveAttachments(prev => prev.filter((_, idx) => idx !== att.removeIdx));
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}

                  {/* Upload / pick buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button" variant="outline" size="sm"
                      className="justify-start text-xs"
                      disabled={isUploadingFile}
                      onClick={() => { setAttachPickerView(null); handleFileUpload('media'); }}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5 text-purple-500" />
                      {isUploadingFile ? "Uploading…" : "Upload from Computer"}
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      className="justify-start text-xs"
                      disabled={isUploadingFile}
                      onClick={() => { setAttachPickerView(null); handleFileUpload('drive'); }}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5 text-green-600" />
                      {isUploadingFile ? "Uploading…" : "Upload from Computer to Drive"}
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      className={`justify-start text-xs ${attachPickerView === 'media' ? 'bg-purple-50 border-purple-200' : ''}`}
                      onClick={() => setAttachPickerView(attachPickerView === 'media' ? null : 'media')}
                    >
                      <FolderOpen className="h-3.5 w-3.5 mr-1.5 text-purple-500" />
                      From Media Library
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      className={`justify-start text-xs ${attachPickerView === 'drive' ? 'bg-green-50 border-green-200' : ''}`}
                      onClick={() => setAttachPickerView(attachPickerView === 'drive' ? null : 'drive')}
                    >
                      <HardDrive className="h-3.5 w-3.5 mr-1.5 text-green-600" />
                      From Google Drive
                    </Button>
                  </div>

                  {/* Media Library picker */}
                  {attachPickerView === 'media' && (
                    <div className="border rounded-md bg-background overflow-hidden">
                      <p className="text-xs font-medium text-muted-foreground px-2 pt-2 pb-1">Pick from Media Library</p>
                      <div className="max-h-36 overflow-y-auto divide-y">
                        {pickerMediaFiles.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-3">No files in your Media Library yet</p>
                        ) : pickerMediaFiles.map(file => (
                          <button
                            key={file.id} type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                            onClick={() => {
                              if (!pendingMediaAttachments.some(a => a.id === file.id)) {
                                setPendingMediaAttachments(prev => [...prev, { id: file.id, name: file.name }]);
                              }
                              setAttachPickerView(null);
                            }}
                          >
                            <FileIcon className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                            <span className="text-sm truncate">{file.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground uppercase">{file.fileType}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Google Drive picker */}
                  {attachPickerView === 'drive' && (
                    <div className="border rounded-md bg-background overflow-hidden">
                      <p className="text-xs font-medium text-muted-foreground px-2 pt-2 pb-1">Pick from Google Drive</p>
                      <div className="max-h-36 overflow-y-auto divide-y">
                        {pickerDriveFiles.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-3">No Drive files found — make sure Google Drive is connected</p>
                        ) : pickerDriveFiles.map(file => (
                          <button
                            key={file.driveFileId} type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                            onClick={() => {
                              if (!pendingDriveAttachments.some(a => a.driveFileId === file.driveFileId)) {
                                setPendingDriveAttachments(prev => [...prev, { driveFileId: file.driveFileId, name: file.name }]);
                              }
                              setAttachPickerView(null);
                            }}
                          >
                            <HardDrive className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            <span className="text-sm truncate">{file.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground">{file.mimeType?.split('.').pop()}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* URL link section */}
                  <div className="space-y-2 border-t pt-3">
                    <p className="text-xs text-muted-foreground">Or add an external link</p>
                    <Input
                      placeholder="Name (e.g. Project Brief, Reference Image)"
                      value={newUrlAttachmentName}
                      onChange={(e) => setNewUrlAttachmentName(e.target.value)}
                    />
                    <Input
                      placeholder="URL (https://...)"
                      value={newUrlAttachmentUrl}
                      onChange={(e) => setNewUrlAttachmentUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); addUrlAttachment(); }
                      }}
                    />
                    <Button
                      type="button" variant="outline" className="w-full"
                      onClick={addUrlAttachment}
                      disabled={!newUrlAttachmentName.trim() || !newUrlAttachmentUrl.trim()}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Link
                    </Button>
                  </div>

                </div>
              )}
            </div>

            {/* ── Add to Calendar section ── */}
            <div className="border rounded-md overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                onClick={() => setWantSchedule(!wantSchedule)}
              >
                <CalendarPlus className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                <span className="text-sm font-medium">Add to Calendar</span>
                <span className="ml-auto text-xs text-muted-foreground">{wantSchedule ? "▲ hide" : "▼ show"}</span>
              </button>
              {wantSchedule && (
                <div className="border-t p-4 space-y-4 bg-muted/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Start Date</Label>
                      <CalendarPicker mode="single" selected={schedDate} onSelect={setSchedDate} className="border rounded-md p-2 bg-background" />
                    </div>
                    {schedDate && (
                      <div>
                        <Label className="text-sm font-medium mb-2 block">
                          {format(schedDate, "EEE, MMM d")} — Existing Events
                        </Label>
                        <div className="border rounded-md divide-y max-h-[240px] overflow-y-auto bg-background">
                          {schedDayEvents.length === 0
                            ? <p className="text-xs text-muted-foreground p-3">No events on this day</p>
                            : schedDayEvents.map((ev: any) => (
                              <div key={ev.id} className="flex items-center gap-2 px-3 py-2">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color || "#3b82f6" }} />
                                <span className="text-xs font-medium text-muted-foreground w-14 flex-shrink-0">
                                  {ev.allDay ? "All day" : format(new Date(ev.startTime), "h:mm a")}
                                </span>
                                <span className="text-xs truncate">{ev.title}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {schedDate && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium mb-1.5 block">Time</Label>
                          <Input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-sm font-medium mb-1.5 block">How Long</Label>
                          <Select value={schedDuration} onValueChange={setSchedDuration}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[{v:"15",l:"15 min"},{v:"30",l:"30 min"},{v:"45",l:"45 min"},{v:"60",l:"1 hour"},{v:"90",l:"1.5 hours"},{v:"120",l:"2 hours"},{v:"180",l:"3 hours"},{v:"240",l:"4 hours"}].map(d => (
                                <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="border rounded-md p-3 bg-background">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium">Push to next day if incomplete</div>
                            <div className="text-xs text-muted-foreground">If not marked done by end of day, automatically moves to the next day</div>
                          </div>
                          <Switch checked={schedRepeat} onCheckedChange={setSchedRepeat} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-create">
                Cancel
              </Button>
              <Button type="submit" disabled={createTaskMutation.isPending} data-testid="button-submit-create-task">
                {createTaskMutation.isPending ? "Creating..." : "Create Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
